import React, { useState, useEffect, useContext, useRef } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSync, faCheckCircle, faTimesCircle, faExclamationTriangle, faChevronDown, faChevronUp, faStop } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

const estadoColors = {
    en_progreso: 'bg-blue-100 text-blue-800 border-blue-300',
    completado: 'bg-green-100 text-green-800 border-green-300',
    error: 'bg-red-100 text-red-800 border-red-300',
    cancelado: 'bg-gray-100 text-gray-700 border-gray-300',
};

const estadoIcons = {
    en_progreso: faSync,
    completado: faCheckCircle,
    error: faTimesCircle,
    cancelado: faExclamationTriangle,
};

const MonitorScraping = () => {
    const { currentUser } = useContext(UserContext);
    const isAdmin = currentUser?.role === 'ADMIN';

    const [logs, setLogs] = useState([]);
    const [logActivo, setLogActivo] = useState(null); // log en_progreso o último
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [ejecutando, setEjecutando] = useState(false);
    const [expandedErrors, setExpandedErrors] = useState({});
    const [expandedSuccesses, setExpandedSuccesses] = useState({});
    const pollingRef = useRef(null);

    const años = [];
    for (let i = new Date().getFullYear(); i >= new Date().getFullYear() - 4; i--) años.push(i);

    // Cargar lista de logs
    const fetchLogs = async () => {
        try {
            const res = await api.get(`/log-scraping?anio=${selectedYear}&limit=10`);
            const lista = res.data.logs || [];
            setLogs(lista);
            // Si /ultimo/:año falla, usar el primer log en_progreso de la lista como fallback
            if (!logActivo) {
                const enProgreso = lista.find(l => l.estado === 'en_progreso');
                if (enProgreso) setLogActivo(enProgreso);
            }
            return lista;
        } catch (err) {
            console.error('Error cargando logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    };

    // Cargar log activo (último del año, para polling)
    const fetchLogActivo = async () => {
        try {
            const res = await api.get(`/log-scraping/ultimo/${selectedYear}`);
            setLogActivo(res.data);
            return res.data;
        } catch {
            // No limpiar logActivo si ya teníamos uno — fetchLogs lo actualizará
            return null;
        }
    };

    // Iniciar polling si hay un proceso en progreso
    const iniciarPolling = () => {
        if (pollingRef.current) return;
        pollingRef.current = setInterval(async () => {
            const [log] = await Promise.all([fetchLogActivo(), fetchLogs()]);
            // Usar el logActivo actual (puede venir de fetchLogs si fetchLogActivo falló)
            const estadoActual = log?.estado;
            if (estadoActual && estadoActual !== 'en_progreso') {
                detenerPolling();
                setEjecutando(false);
            }
        }, 3000); // cada 3 segundos
    };

    const detenerPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    useEffect(() => {
        // Arrancar ambas cargas. Si fetchLogActivo falla, fetchLogs provee el fallback.
        Promise.all([fetchLogs(), fetchLogActivo()]).then(([lista, log]) => {
            const activo = log || lista?.find(l => l.estado === 'en_progreso');
            if (activo?.estado === 'en_progreso') {
                setLogActivo(activo);
                setEjecutando(true);
                iniciarPolling();
            }
        });
        return () => detenerPolling();
    }, [selectedYear]);

    const handleEjecutar = async () => {
        if (!isAdmin) return;

        const { value: confirmed } = await Swal.fire({
            title: '¿Iniciar consulta SII?',
            html: `Se consultará el estado de <strong>todas las empresas con renta</strong> para el año <strong>${selectedYear}</strong> y los 2 anteriores.<br><br>
                   <small class="text-gray-500">Cada empresa inicia sesión con sus propias credenciales. El proceso puede tardar varios minutos.</small>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, iniciar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb',
        });

        if (!confirmed) return;

        try {
            setEjecutando(true);
            await api.post('/estados-sii/ejecutar', { año: selectedYear });
            toast.success('Proceso iniciado en el servidor');
            // Esperar 3 segundos para que el log se cree en BD
            setTimeout(async () => {
                await fetchLogActivo();
                await fetchLogs();
                iniciarPolling();
            }, 3000);
        } catch (err) {
            setEjecutando(false);
            toast.error('Error al iniciar: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleCancelar = async () => {
        const { value: confirmed } = await Swal.fire({
            title: '¿Detener el proceso?',
            text: 'Se detendrá el scraping. Los datos ya procesados se conservan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, detener',
            cancelButtonText: 'No, continuar',
            confirmButtonColor: '#dc2626',
        });
        if (!confirmed) return;
        try {
            // forzar-cancelar: marca en DB Y envía señal — garantiza que el proceso libera el lock
            await api.post('/estados-sii/forzar-cancelar');
            toast.success('Proceso detenido correctamente.');
            detenerPolling();
            setEjecutando(false);
            setTimeout(() => { fetchLogs(); fetchLogActivo(); }, 1500);
        } catch (err) {
            toast.error('Error al cancelar: ' + (err.response?.data?.error || err.message));
        }
    };

    const toggleErrors = (id) => {
        setExpandedErrors(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSuccesses = (id) => {
        setExpandedSuccesses(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatDuracion = (ms) => {
        if (!ms) return '-';
        const min = Math.floor(ms / 60000);
        const seg = Math.floor((ms % 60000) / 1000);
        return `${min}m ${seg}s`;
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-5xl">
            {/* Header */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <div className="flex justify-between items-center mb-2">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Monitor de Consultas SII</h1>
                        <p className="text-sm text-gray-500 mt-1">Seguimiento en tiempo real del proceso de scraping de declaraciones de renta.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={selectedYear}
                            onChange={e => setSelectedYear(Number(e.target.value))}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                            {años.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                {logActivo?.estado === 'en_progreso' && (
                                    <button
                                        onClick={handleCancelar}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faStop} />
                                        Detener
                                    </button>
                                )}
                                <button
                                    onClick={handleEjecutar}
                                    disabled={logActivo?.estado === 'en_progreso'}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                                        logActivo?.estado === 'en_progreso' ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={logActivo?.estado === 'en_progreso' ? faSync : faPlay} className={logActivo?.estado === 'en_progreso' ? 'animate-spin' : ''} />
                                    {logActivo?.estado === 'en_progreso' ? 'Ejecutando...' : 'Iniciar Consulta'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Log activo / En progreso */}
            {logActivo && logActivo.estado === 'en_progreso' && (
                <div className="bg-blue-50 border border-blue-300 rounded-lg p-5 mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faSync} className="animate-spin text-blue-600" />
                            <span className="font-semibold text-blue-800 text-lg">Proceso en ejecución</span>
                            <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">
                                {logActivo.tipoEjecucion}
                            </span>
                        </div>
                        <span className="text-xs text-blue-600">
                            Inicio: {new Date(logActivo.fechaInicio).toLocaleString('es-CL')}
                        </span>
                    </div>

                    {/* Acción actual */}
                    {(logActivo.accionActual || logActivo.empresaActual) && (
                        <div className="mb-3 flex items-center gap-2 bg-blue-100 rounded-lg px-4 py-2 text-sm text-blue-800">
                            <FontAwesomeIcon icon={faSync} className="animate-spin text-blue-500 flex-shrink-0" size="xs" />
                            <span>
                                {logActivo.empresaActual && <strong className="mr-1">{logActivo.empresaActual}:</strong>}
                                {logActivo.accionActual || 'Procesando...'}
                            </span>
                        </div>
                    )}

                    {/* Barra de progreso */}
                    <div className="mb-2">
                        <div className="flex justify-between text-sm text-blue-700 mb-1">
                            <span>Empresas procesadas</span>
                            <span className="font-bold">{logActivo.procesados} / {logActivo.totalProcesar}</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-4 overflow-hidden">
                            <div
                                className="bg-blue-600 h-4 rounded-full transition-all duration-500"
                                style={{ width: logActivo.totalProcesar > 0 ? `${(logActivo.procesados / logActivo.totalProcesar) * 100}%` : '0%' }}
                            />
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-3 text-center">
                        <div className="bg-white rounded-lg p-3">
                            <div className="text-2xl font-bold text-green-600">{logActivo.exitosos}</div>
                            <div className="text-xs text-gray-500">Exitosas</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                            <div className="text-2xl font-bold text-red-500">{logActivo.fallidos}</div>
                            <div className="text-xs text-gray-500">Con error</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                            <div className="text-2xl font-bold text-gray-600">{logActivo.totalProcesar - logActivo.procesados}</div>
                            <div className="text-xs text-gray-500">Pendientes</div>
                        </div>
                    </div>

                    {/* Errores actuales */}
                    {logActivo.errores?.length > 0 && (
                        <div className="mt-4">
                            <button
                                onClick={() => toggleErrors(logActivo._id)}
                                className="flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800"
                            >
                                <FontAwesomeIcon icon={expandedErrors[logActivo._id] ? faChevronUp : faChevronDown} size="xs" />
                                Ver {logActivo.errores.length} error(es) registrado(s)
                            </button>
                            {expandedErrors[logActivo._id] && (
                                <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-red-200 rounded-lg">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-red-50 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Empresa</th>
                                                <th className="px-3 py-2 text-left">RUT</th>
                                                <th className="px-3 py-2 text-left">Error</th>
                                                <th className="px-3 py-2 text-left">Hora</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logActivo.errores.map((e, i) => (
                                                <tr key={i} className="border-t border-red-100">
                                                    <td className="px-3 py-2">{e.nombreCliente}</td>
                                                    <td className="px-3 py-2">{e.rut}</td>
                                                    <td className="px-3 py-2 text-red-600">{e.error}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString('es-CL') : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Exitosos actuales */}
                    {logActivo.exitososDetalle?.length > 0 && (
                        <div className="mt-4">
                            <button
                                onClick={() => toggleSuccesses(logActivo._id)}
                                className="flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-800"
                            >
                                <FontAwesomeIcon icon={expandedSuccesses[logActivo._id] ? faChevronUp : faChevronDown} size="xs" />
                                Ver {logActivo.exitososDetalle.length} empresa(s) exitosa(s)
                            </button>
                            {expandedSuccesses[logActivo._id] && (
                                <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-green-200 rounded-lg">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-green-50 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Empresa</th>
                                                <th className="px-3 py-2 text-left">RUT</th>
                                                <th className="px-3 py-2 text-left">Detalle</th>
                                                <th className="px-3 py-2 text-left">Hora</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logActivo.exitososDetalle.map((e, i) => (
                                                <tr key={i} className="border-t border-green-100">
                                                    <td className="px-3 py-2">{e.nombreCliente}</td>
                                                    <td className="px-3 py-2">{e.rut}</td>
                                                    <td className="px-3 py-2 text-green-700">
                                                        {(e.estados || []).map(est => `${est.año}: ${est.estado}`).join(' · ')}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString('es-CL') : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Log en tiempo real */}
                    {logActivo.logEntries?.length > 0 && (
                        <div className="mt-4">
                            <div className="text-xs font-semibold text-blue-700 mb-1 uppercase tracking-wide">Log del proceso</div>
                            <div className="bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs">
                                {[...logActivo.logEntries].reverse().map((line, i) => (
                                    <div key={i} className={`py-0.5 ${
                                        line.includes('✅') ? 'text-green-400' :
                                        line.includes('❌') ? 'text-red-400' :
                                        line.includes('⚠️') ? 'text-yellow-400' :
                                        line.includes('⏳') || line.includes('🔄') || line.includes('📊') ? 'text-blue-300' :
                                        'text-gray-300'
                                    }`}>{line}</div>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="text-xs text-blue-500 mt-3">Actualizando cada 3 segundos...</p>
                </div>
            )}

            {/* Historial de ejecuciones */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-700">Historial de Ejecuciones</h2>
                </div>

                {loadingLogs ? (
                    <div className="text-center py-10 text-gray-400">Cargando logs...</div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">
                        No hay ejecuciones registradas para {selectedYear}.<br />
                        <span className="text-sm">Inicia una consulta manual para comenzar.</span>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {logs.map(log => {
                            const estadoClass = estadoColors[log.estado] || estadoColors.cancelado;
                            const estadoIcon = estadoIcons[log.estado] || faExclamationTriangle;
                            const porcentaje = log.totalProcesar > 0 ? Math.round((log.procesados / log.totalProcesar) * 100) : 0;

                            return (
                                <div key={log._id} className="p-5">
                                    {/* Cabecera del log */}
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-3">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${estadoClass}`}>
                                                <FontAwesomeIcon icon={estadoIcon} size="xs" className={log.estado === 'en_progreso' ? 'animate-spin' : ''} />
                                                {log.estado === 'en_progreso' ? 'En progreso' :
                                                 log.estado === 'completado' ? 'Completado' :
                                                 log.estado === 'error' ? 'Error' : 'Cancelado'}
                                            </span>
                                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
                                                {log.tipoEjecucion}
                                            </span>
                                            {log.usuarioInicio && (
                                                <span className="text-xs text-gray-400">por {log.usuarioInicio}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {isAdmin && log.estado === 'en_progreso' && (
                                                <button
                                                    onClick={handleCancelar}
                                                    className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon={faStop} size="xs" />
                                                    Detener
                                                </button>
                                            )}
                                            <div className="text-right text-xs text-gray-400">
                                                <div>{new Date(log.fechaInicio).toLocaleString('es-CL')}</div>
                                                {log.duracion && <div>Duración: {formatDuracion(log.duracion)}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Barra de progreso */}
                                    <div className="mb-2">
                                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                                            <span>{log.procesados} de {log.totalProcesar} empresas</span>
                                            <span>{porcentaje}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${log.estado === 'completado' ? 'bg-green-500' : log.estado === 'error' ? 'bg-red-400' : 'bg-blue-500'}`}
                                                style={{ width: `${porcentaje}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="flex gap-4 text-xs text-gray-500 mt-2">
                                        <span className="text-green-600 font-medium">✓ {log.exitosos} exitosas</span>
                                        <span className="text-red-500 font-medium">✗ {log.fallidos} con error</span>
                                        {log.mensaje && <span className="text-gray-400 italic">{log.mensaje}</span>}
                                    </div>

                                    {/* Errores colapsables */}
                                    {log.errores?.length > 0 && (
                                        <div className="mt-3">
                                            <button
                                                onClick={() => toggleErrors(log._id)}
                                                className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700"
                                            >
                                                <FontAwesomeIcon icon={expandedErrors[log._id] ? faChevronUp : faChevronDown} size="xs" />
                                                {expandedErrors[log._id] ? 'Ocultar' : 'Ver'} {log.errores.length} error(es)
                                            </button>
                                            {expandedErrors[log._id] && (
                                                <div className="mt-2 max-h-48 overflow-y-auto bg-red-50 border border-red-100 rounded-lg">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-red-100 sticky top-0">
                                                            <tr>
                                                                <th className="px-3 py-1.5 text-left">Empresa</th>
                                                                <th className="px-3 py-1.5 text-left">RUT</th>
                                                                <th className="px-3 py-1.5 text-left">Error</th>
                                                                <th className="px-3 py-1.5 text-left">Hora</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {log.errores.map((e, i) => (
                                                                <tr key={i} className="border-t border-red-100">
                                                                    <td className="px-3 py-1.5 font-medium">{e.nombreCliente}</td>
                                                                    <td className="px-3 py-1.5">{e.rut}</td>
                                                                    <td className="px-3 py-1.5 text-red-600">{e.error}</td>
                                                                    <td className="px-3 py-1.5 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString('es-CL') : '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Exitosos colapsables */}
                                    {log.exitososDetalle?.length > 0 && (
                                        <div className="mt-3">
                                            <button
                                                onClick={() => toggleSuccesses(log._id)}
                                                className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800"
                                            >
                                                <FontAwesomeIcon icon={expandedSuccesses[log._id] ? faChevronUp : faChevronDown} size="xs" />
                                                {expandedSuccesses[log._id] ? 'Ocultar' : 'Ver'} {log.exitososDetalle.length} empresa(s) exitosa(s)
                                            </button>
                                            {expandedSuccesses[log._id] && (
                                                <div className="mt-2 max-h-48 overflow-y-auto bg-green-50 border border-green-100 rounded-lg">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-green-100 sticky top-0">
                                                            <tr>
                                                                <th className="px-3 py-1.5 text-left">Empresa</th>
                                                                <th className="px-3 py-1.5 text-left">RUT</th>
                                                                <th className="px-3 py-1.5 text-left">Detalle</th>
                                                                <th className="px-3 py-1.5 text-left">Hora</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {log.exitososDetalle.map((e, i) => (
                                                                <tr key={i} className="border-t border-green-100">
                                                                    <td className="px-3 py-1.5 font-medium">{e.nombreCliente}</td>
                                                                    <td className="px-3 py-1.5">{e.rut}</td>
                                                                    <td className="px-3 py-1.5 text-green-700">
                                                                        {(e.estados || []).map(est => `${est.año}: ${est.estado}`).join(' · ')}
                                                                    </td>
                                                                    <td className="px-3 py-1.5 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString('es-CL') : '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonitorScraping;
